import { useState, useCallback, useMemo } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { AdminListPanel } from '../../admin/AdminListPanel';
import { useProtocols, useDeleteProtocol } from './useProtocols';
import { gwtStyles } from '../../../config/theme';
import { pushError, pushSuccess } from '../../../stores/notificationStore';
import { useAuth } from '../../auth/useAuth';
import type { BritProtocol } from '../../../api/types';

/** Flatten a Record<string, BritProtocol> into an array with computed columns */
interface ProtocolRow {
  id: string;
  name: string;
  scope: 'user' | 'site';
  modality: string;
  bodyPart: string;
  steps: number;
}

function toRows(data: Record<string, BritProtocol> | undefined): ProtocolRow[] {
  if (!data) return [];
  return Object.values(data).map((p) => {
    // Extract modality and bodyPart from STUDY-type content elements
    let modality = '';
    let bodyPart = '';
    for (const el of p.content?.elements ?? []) {
      if (el.ctype === 'STUDY') {
        for (const ident of el.identifiers) {
          if (ident.type === 'MODALITY' && ident.val) modality = ident.val;
          if (ident.type === 'BODYPART' && ident.val) bodyPart = ident.val;
        }
      }
    }
    return {
      id: p.id,
      name: p.name,
      scope: p.scope ?? 'user',
      modality,
      bodyPart,
      steps: p.steps?.length ?? 0,
    };
  });
}

const columns: GridColDef<ProtocolRow>[] = [
  { field: 'name', headerName: 'Name', flex: 1.5, minWidth: 160 },
  { field: 'modality', headerName: 'Modality', width: 90 },
  { field: 'bodyPart', headerName: 'Body Part', width: 110 },
  { field: 'steps', headerName: 'Steps', width: 60, type: 'number' },
  {
    field: 'scope',
    headerName: 'Scope',
    width: 80,
    renderCell: (params) => (
      <Chip
        label={params.value === 'site' ? 'Site' : 'User'}
        size="small"
        color={params.value === 'site' ? 'primary' : 'default'}
        variant="outlined"
        sx={{ height: 20, fontSize: 11 }}
      />
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 50,
    sortable: false,
    renderCell: () => null, // populated dynamically via column override
  },
];

/**
 * Protocol management panel — displays user and site hanging protocols.
 * Registered as an admin tab, accessible to all users.
 * Admins can see and delete site-scoped protocols; regular users see only their own.
 */
export const ProtocolsPanel = () => {
  const { data, isLoading, refetch } = useProtocols('all');
  const deleteMutation = useDeleteProtocol();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMINISTRATOR';

  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<ProtocolRow | null>(null);

  const rows = useMemo(() => {
    const all = toRows(data);
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (r) =>
        r.name.toLowerCase().includes(s) ||
        r.modality.toLowerCase().includes(s) ||
        r.bodyPart.toLowerCase().includes(s) ||
        r.scope.includes(s),
    );
  }, [data, search]);

  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleDelete = useCallback(
    async (row: ProtocolRow) => {
      try {
        await deleteMutation.mutateAsync({ id: row.id, scope: row.scope });
        pushSuccess(`Protocol "${row.name}" deleted`);
      } catch (e) {
        pushError(`Failed to delete protocol: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setConfirmDelete(null);
      }
    },
    [deleteMutation],
  );

  const effectiveColumns = useMemo(
    () =>
      columns.map((col) =>
        col.field === 'actions'
          ? {
              ...col,
              renderCell: (params: { row: ProtocolRow }) => {
                const row = params.row;
                // Non-admins can only delete their own (user-scope) protocols
                if (row.scope === 'site' && !isAdmin) return null;
                return (
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDelete(row)}
                    sx={{ width: 24, height: 24 }}
                  >
                    <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  </IconButton>
                );
              },
            }
          : col,
      ),
    [isAdmin],
  );

  return (
    <>
      <AdminListPanel
        title="Hanging Protocols"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        statusText={`${rows.length} protocol${rows.length !== 1 ? 's' : ''}`}
      >
        <DataGrid
          rows={rows}
          columns={effectiveColumns}
          getRowId={(row) => row.id}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            border: 'none',
            fontSize: 12,
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Delete Protocol
            </Typography>
            <IconButton size="small" onClick={() => setConfirmDelete(null)}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Delete protocol <strong>{confirmDelete.name}</strong>
              {confirmDelete.scope === 'site' ? ' (site-level)' : ''}? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => void handleDelete(confirmDelete)}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
